import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Crown, Star, Zap, Gift, Shield, Loader2 } from 'lucide-react';
import { addVipPlanToCart, fetchVipPlans, type VipPlanType } from '../lib/vipApi';

interface VIPUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** After VIP line is added to cart */
  onAddedToCart?: () => void | Promise<void>;
  message?: string;
}

export default function VIPUpgradeModal({
  isOpen,
  onClose,
  onAddedToCart,
  message = "You've used all 10 free swipes today. Upgrade to VIP for unlimited swiping!",
}: VIPUpgradeModalProps) {
  const [plan, setPlan] = useState<VipPlanType>('monthly');
  const [monthlyInr, setMonthlyInr] = useState('299');
  const [yearlyInr, setYearlyInr] = useState('2999');
  const [monthlyPid, setMonthlyPid] = useState<number | null>(null);
  const [yearlyPid, setYearlyPid] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;

  useEffect(() => {
    if (!isOpen || !token) return;
    let cancelled = false;
    (async () => {
      const res = await fetchVipPlans(token);
      if (cancelled || !res.success || !res.data) return;
      setMonthlyInr(res.data.monthly.amount_inr);
      setYearlyInr(res.data.yearly.amount_inr);
      if (typeof res.data.monthly.product_id === 'number') setMonthlyPid(res.data.monthly.product_id);
      if (typeof res.data.yearly.product_id === 'number') setYearlyPid(res.data.yearly.product_id);
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, token]);

  if (!isOpen) return null;

  const benefits = [
    { icon: Zap, text: 'Unlimited daily swipes', color: 'text-yellow-400' },
    { icon: Gift, text: 'Exclusive deals & live drops', color: 'text-pink-400' },
    { icon: Star, text: 'Richer feed & mystery mix', color: 'text-blue-400' },
    { icon: Shield, text: 'Return credit on eligible VIP plan', color: 'text-green-400' },
  ];

  const handleAddToCart = async () => {
    if (!token) {
      toast.error('Sign in required');
      return;
    }
    const pid = plan === 'monthly' ? monthlyPid : yearlyPid;
    if (pid == null) {
      toast.error('Still loading plans — try again.');
      return;
    }
    setLoading(true);
    try {
      const res = await addVipPlanToCart(token, pid);
      if (res.success) {
        toast.success('VIP added to cart — checkout to pay and activate.');
        await onAddedToCart?.();
        onClose();
      } else {
        toast.error(res.message || 'Could not add VIP to cart');
      }
    } finally {
      setLoading(false);
    }
  };

  const displayPrice = plan === 'monthly' ? monthlyInr : yearlyInr;
  const priceLabel = plan === 'monthly' ? 'per month' : 'per year';

  return (
    <>
      <div
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      />

      <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-cosmic-background border border-cosmic-border rounded-2xl p-4 sm:p-6 max-w-sm sm:max-w-md w-full mx-4 z-50 shadow-2xl max-h-[85vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-transparent">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-2 right-2 sm:top-4 sm:right-4 text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-800 rounded-full min-w-[44px] min-h-[44px] flex items-center justify-center"
          aria-label="Close modal"
        >
          <span className="text-lg">✕</span>
        </button>

        <div className="text-center mb-4 sm:mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full mb-3 sm:mb-4">
            <Crown size={32} className="sm:w-10 sm:h-10 text-black" />
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">Jaalu VIP</h2>
          <p className="text-gray-400 text-xs sm:text-sm px-2">{message}</p>
        </div>

        <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
          {benefits.map((benefit, index) => (
            <div key={index} className="flex items-center gap-2 sm:gap-3">
              <div className={`p-1.5 sm:p-2 rounded-lg bg-gray-800 ${benefit.color} flex-shrink-0`}>
                <benefit.icon size={16} className="sm:w-5 sm:h-5" />
              </div>
              <span className="text-white text-xs sm:text-sm">{benefit.text}</span>
            </div>
          ))}
        </div>

        <div className="flex gap-2 mb-4">
          <button
            type="button"
            onClick={() => setPlan('monthly')}
            className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
              plan === 'monthly'
                ? 'border-amber-400 bg-amber-500/15 text-white'
                : 'border-gray-600 text-gray-400 hover:border-gray-500'
            }`}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setPlan('yearly')}
            className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
              plan === 'yearly'
                ? 'border-amber-400 bg-amber-500/15 text-white'
                : 'border-gray-600 text-gray-400 hover:border-gray-500'
            }`}
          >
            Yearly
          </button>
        </div>

        <div className="text-center mb-4 sm:mb-6 p-3 sm:p-4 bg-cosmic-card border border-cosmic-border rounded-lg">
          <div className="text-2xl sm:text-3xl font-bold text-cosmic-primary mb-1">
            ₹{Number(displayPrice).toLocaleString('en-IN')}
          </div>
          <div className="text-gray-400 text-xs sm:text-sm">{priceLabel}</div>
          <p className="text-xs text-gray-500 mt-2">Pay at checkout with your order — VIP activates after payment.</p>
        </div>

        <div className="space-y-2 sm:space-y-3">
          <button
            type="button"
            onClick={handleAddToCart}
            disabled={loading}
            className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-semibold py-2.5 sm:py-3 px-4 sm:px-6 rounded-lg hover:from-orange-500 hover:to-yellow-400 transition-all duration-300 flex items-center justify-center gap-2 text-sm sm:text-base disabled:opacity-60"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Crown size={18} className="sm:w-5 sm:h-5" />}
            <span>{loading ? 'Adding…' : 'Add VIP to cart'}</span>
          </button>

          <button
            type="button"
            onClick={onClose}
            className="w-full bg-transparent border border-gray-600 text-gray-300 py-2.5 sm:py-3 px-4 sm:px-6 rounded-lg hover:bg-gray-800 transition-colors text-sm sm:text-base"
          >
            Not now
          </button>
        </div>
      </div>
    </>
  );
}
